import React from "react";
import { RolePage } from "./rolePage";
import { RoleForm } from "./roleForm";
import { RoleView } from "./roleView";
import { GRID_ACTION_TYPES, GridActionType } from "../../../assets/types";
import { isGridActionType } from "../../../assets/utils/helpers";
import { RoleService } from "./roleService";
import { Constants } from "../../../assets/utils/lib/constants";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { RoleClaimDto, RoleDto } from "../../../services/dto";
import { useQueryClient } from "@tanstack/react-query";
import { RolePermissionContainer } from "./permission/rolePermissionContainer";
import { RolePermissionService } from "./permission/rolePermissionService";
import { apiErrorMessage } from "../../../assets/utils/lib/error";
import { useActionFromQuery } from "../../../hooks/useActionFromQuery";

export const RoleContainer = () => {
  const queryClient = useQueryClient();
  const [data, setData] = React.useState<RoleDto>();
  const [drawerType, setDrawerType] = React.useState<GridActionType>(
    GRID_ACTION_TYPES.HIDDEN
  );

  const { showSnackbar } = useSnackbar();

  const roleQuery = RoleService.getRoleQuery(data?.id ?? undefined);
  const { mutateAsync: saveRole } = RoleService.save();

  const isEditMode = !!(data?.id && data.id !== Constants.emptyGuid);

  const handleSubmit = async (values: RoleDto) => {
    values.id = isEditMode ? data!.id : Constants.emptyGuid;
    const message = isEditMode ? "Role updated" : "Role saved";
    return await saveRole(values, {
      onSuccess: (response) => {
        const errorMessage = apiErrorMessage(response.errorMessages);
        if (errorMessage) {
          showSnackbar(errorMessage, MessageSeverity.Error);
        } else {
          showSnackbar(message, MessageSeverity.Success);
          isEditMode && roleQuery.refetch();
          queryClient.invalidateQueries({ queryKey: ["roleGridQuery"] });
          closeDrawer();
        }
      },
    });
  };

  const { mutateAsync: saveRolePermissions } = RolePermissionService.save();

  const handleRolePermissionSubmit = async (values: RoleClaimDto) => {
    return await saveRolePermissions(values, {
      onSuccess: (response) => {
        const errorMessage = apiErrorMessage(response.errorMessages);
        if (errorMessage) {
          showSnackbar(errorMessage, MessageSeverity.Error);
        } else {
          showSnackbar("Role permissions saved", MessageSeverity.Success);
          queryClient.refetchQueries({
            queryKey: ["getRolePermissionQuery", values.roleId],
          });
          closeDrawer();
        }
      },
    });
  };

  const handleCustomEvent = (actionType: string, dataId: string): void => {
    if (isGridActionType(actionType)) {
      setDrawerType(actionType);
      setData({ id: dataId } as RoleDto);
      setSearchAction(actionType, dataId);
    }
  };

  const { setSearchAction, clearSearchAction } = useActionFromQuery(handleCustomEvent);

  const closeDrawer = (): void =>{
      setDrawerType(GRID_ACTION_TYPES.HIDDEN);
      clearSearchAction();
  }

  const handleEdit = (): void => {
    setData(roleQuery.data);
    setDrawerType(GRID_ACTION_TYPES.EDIT);
    setSearchAction(GRID_ACTION_TYPES.EDIT, roleQuery.data?.id);
  };

  const renderDrawerContent = (): React.JSX.Element | null => {
    switch (drawerType) {
      case GRID_ACTION_TYPES.NEW:
        return (
          <RoleForm show={true} onHide={closeDrawer} onSave={handleSubmit} />
        );
      case GRID_ACTION_TYPES.EDIT:
        return (
          <RoleForm
            data={roleQuery?.data}
            show={!!roleQuery?.data}
            loading={roleQuery.isLoading}
            onHide={closeDrawer}
            onSave={handleSubmit}
          />
        );
      case GRID_ACTION_TYPES.VIEW:
      case GRID_ACTION_TYPES.DOUBLE_CLICK:
        return (
          <RoleView
            data={roleQuery?.data}
            show={!!roleQuery?.data}
            onHide={closeDrawer}
            onEdit={handleEdit}
          />
        );
      case GRID_ACTION_TYPES.PERMISSION:
        return (
          <RolePermissionContainer
            id={data?.id}
            onHide={closeDrawer}
            onSave={handleRolePermissionSubmit}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <RolePage onCustomEvent={handleCustomEvent} />
      {renderDrawerContent()}
    </>
  );
};
